import {
  CHARLES_PRESSURE_KPA,
  EXPLORER_RANGES,
  KELVIN_OFFSET,
  boyleSeries,
  charlesSeries,
  formatSig,
  particleModel,
  pressureTemperatureSeries,
  type ExplorerState,
  type GasMode,
  type GraphPoint,
} from './gasLaws';
import { useAnimationTime } from './useAnimationTime';

const BOX_VIEW = { width: 240, height: 150, left: 10, top: 18, boxHeight: 112 } as const;
const BOX_MIN_WIDTH = 30;
const BOX_MAX_WIDTH = 210;
const PARTICLE_RADIUS = 4;
/** Display speed in SVG units per second at the reference temperature. */
const BASE_DISPLAY_SPEED = 46;
const GOLDEN_FRACTION = 0.618034;
const GOLDEN_ANGLE = 2.399963;
const READOUT_BAR_MAX = 4;

function fraction(value: number): number {
  return value - Math.floor(value);
}

/** Position of a particle bouncing elastically between 0 and `span`. */
function bounce(start: number, velocity: number, time: number, span: number): number {
  if (span <= 0) return 0;
  const period = 2 * span;
  const travelled = (((start + velocity * time) % period) + period) % period;
  return travelled <= span ? travelled : period - travelled;
}

export function ParticleBox({
  mode,
  state,
  running,
  maxVolumeL = EXPLORER_RANGES.volumeL.max,
}: {
  mode: GasMode;
  state: ExplorerState;
  running: boolean;
  /** Volume drawn as the widest box; the Explore view uses its larger volume range. */
  maxVolumeL?: number;
}) {
  const time = useAnimationTime(running);
  const model = particleModel(state);
  const boxWidth =
    BOX_MIN_WIDTH +
    ((BOX_MAX_WIDTH - BOX_MIN_WIDTH) * Math.min(state.volumeL, maxVolumeL)) / maxVolumeL;
  const innerWidth = boxWidth - 2 * PARTICLE_RADIUS;
  const innerHeight = BOX_VIEW.boxHeight - 2 * PARTICLE_RADIUS;
  const speed = BASE_DISPLAY_SPEED * model.relativeSpeed;
  const movablePiston = mode !== 'ideal';

  return (
    <figure className="rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky to-canvas-pure p-4">
      <svg
        viewBox={`0 0 ${BOX_VIEW.width} ${BOX_VIEW.height}`}
        className="w-full"
        role="img"
        aria-label={`Particle model: ${model.particleCount} particles in ${formatSig(state.volumeL, 3)} L at ${formatSig(state.temperatureK, 4)} K. Mean speed ${model.relativeSpeed.toFixed(2)} times and wall-collision rate ${model.relativeCollisionRate.toFixed(2)} times the reference.`}
      >
        <rect
          x={BOX_VIEW.left}
          y={BOX_VIEW.top}
          width={boxWidth}
          height={BOX_VIEW.boxHeight}
          rx="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-ink-soft transition-all duration-300 motion-reduce:transition-none"
        />
        {movablePiston ? (
          <g className="text-brand-coral">
            <rect
              x={BOX_VIEW.left + boxWidth - 2}
              y={BOX_VIEW.top - 6}
              width="6"
              height={BOX_VIEW.boxHeight + 12}
              rx="2"
              fill="currentColor"
            />
            <text
              x={Math.min(BOX_VIEW.left + boxWidth + 8, BOX_VIEW.width - 4)}
              y={BOX_VIEW.top + 4}
              fontSize="9"
              fontWeight="700"
              fill="currentColor"
              textAnchor={boxWidth > BOX_MAX_WIDTH - 20 ? 'end' : 'start'}
            >
              piston
            </text>
          </g>
        ) : null}
        {Array.from({ length: model.particleCount }, (_, index) => {
          const angle = index * GOLDEN_ANGLE;
          const startX = fraction(GOLDEN_FRACTION * (index + 1) + 0.13) * innerWidth;
          const startY = fraction(GOLDEN_FRACTION * 1.7 * (index + 1) + 0.29) * innerHeight;
          const x = bounce(startX, speed * Math.cos(angle), time, innerWidth);
          const y = bounce(startY, speed * Math.sin(angle), time, innerHeight);
          return (
            <circle
              key={index}
              cx={BOX_VIEW.left + PARTICLE_RADIUS + x}
              cy={BOX_VIEW.top + PARTICLE_RADIUS + y}
              r={PARTICLE_RADIUS}
              className={index % 2 ? 'fill-brand-sky' : 'fill-brand-coral'}
            />
          );
        })}
        <text
          x={BOX_VIEW.left}
          y="12"
          fontSize="9"
          fontWeight="700"
          fill="currentColor"
          className="text-slate2"
        >
          {mode === 'ideal'
            ? 'Rigid container'
            : mode === 'boyle'
              ? 'Constant temperature'
              : `Constant pressure ${CHARLES_PRESSURE_KPA} kPa`}
        </text>
        <text
          x={BOX_VIEW.left}
          y={BOX_VIEW.height - 6}
          fontSize="9"
          fontWeight="700"
          fill="currentColor"
          className="text-slate2"
        >
          {`V = ${formatSig(state.volumeL, 3)} L · T = ${formatSig(state.temperatureK, 4)} K · n = ${formatSig(state.nMol, 2)} mol`}
        </text>
      </svg>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Readout
          label="Pressure"
          value={`${formatSig(model.pressureKPa, 3)} kPa`}
          ratio={model.pressureKPa / 100}
          tone="bg-brand-coral"
        />
        <Readout
          label="Mean speed"
          value={`× ${model.relativeSpeed.toFixed(2)}`}
          ratio={model.relativeSpeed}
          tone="bg-brand-sky"
        />
        <Readout
          label="Wall hits per second"
          value={`× ${model.relativeCollisionRate.toFixed(2)}`}
          ratio={model.relativeCollisionRate}
          tone="bg-brand-mint"
        />
      </div>
      <figcaption className="mt-3 text-xs font-medium leading-5 text-slate2">
        Speeds follow √T and wall hits follow (n ÷ V)√T, both compared with 0.200 mol at 298.15 K in
        5.00 L. Particles are not to scale.
      </figcaption>
    </figure>
  );
}

function Readout({
  label,
  value,
  ratio,
  tone,
}: {
  label: string;
  value: string;
  ratio: number;
  tone: string;
}) {
  const width = Math.min(100, (Math.max(0, ratio) / READOUT_BAR_MAX) * 100);
  return (
    <div className="rounded-2xl bg-white/85 px-3 py-2">
      <p className="text-[0.65rem] font-extrabold uppercase tracking-[0.1em] text-slate2">
        {label}
      </p>
      <p className="font-extrabold tabular-nums text-ink">{value}</p>
      <div className="mt-1 h-1.5 rounded-full bg-surface" aria-hidden="true">
        <div className={`h-1.5 rounded-full ${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

const PLOT = { left: 46, right: 306, top: 14, bottom: 160 } as const;
const AXIS_ROUNDING = 100;
const AXIS_ROUNDING_SMALL = 2;
const MEASURED_FROM_K = EXPLORER_RANGES.temperatureK.min;

interface GraphSpec {
  label: string;
  xLabel: string;
  yLabel: string;
  xMin: number;
  xMax: number;
  yMax: number;
  points: GraphPoint[];
  dashedBelowX: number | null;
  current: GraphPoint;
  xTicks: number[];
}

function niceMax(value: number, rounding: number): number {
  return Math.ceil(value / rounding) * rounding;
}

/** Axis maxima; the Explore view widens them to its larger input ranges. */
export interface GasGraphAxis {
  volumeMaxL: number;
  temperatureMaxK: number;
}

const DEFAULT_AXIS: GasGraphAxis = {
  volumeMaxL: EXPLORER_RANGES.volumeL.max,
  temperatureMaxK: EXPLORER_RANGES.temperatureK.max,
};

function graphSpec(
  mode: GasMode,
  state: ExplorerState,
  celsius: boolean,
  axis: GasGraphAxis,
): GraphSpec {
  if (mode === 'boyle') {
    const points = boyleSeries(
      state.nMol,
      state.temperatureK,
      Math.min(EXPLORER_RANGES.volumeL.min, state.volumeL),
      axis.volumeMaxL,
    );
    const current = { x: state.volumeL, y: particleModel(state).pressureKPa };
    return {
      label: 'Model pressure–volume graph at constant temperature',
      xLabel: 'Volume (L)',
      yLabel: 'p (kPa)',
      xMin: 0,
      xMax: axis.volumeMaxL,
      yMax: niceMax(Math.max(...points.map((point) => point.y)), AXIS_ROUNDING),
      points,
      dashedBelowX: null,
      current,
      xTicks: Array.from({ length: 6 }, (_, index) => (axis.volumeMaxL * index) / 5),
    };
  }
  const offset = celsius ? KELVIN_OFFSET : 0;
  const shift = (points: GraphPoint[]) =>
    points.map((point) => ({ x: point.x - offset, y: point.y }));
  const xMin = celsius ? -300 : 0;
  const temperatureMax = axis.temperatureMaxK;
  const xMax = temperatureMax - offset;
  const parts = temperatureMax === 600 ? 3 : 4;
  const xTicks = celsius
    ? [-273.15, 0, Math.floor(xMax / 100) * 100]
    : Array.from({ length: parts + 1 }, (_, index) => (temperatureMax * index) / parts);
  if (mode === 'charles') {
    const points = shift(charlesSeries(state.nMol, CHARLES_PRESSURE_KPA, temperatureMax));
    return {
      label: celsius
        ? 'Model volume against Celsius temperature, extrapolated to absolute zero'
        : 'Model volume against kelvin temperature at constant pressure',
      xLabel: celsius ? 'Temperature (°C)' : 'Temperature (K)',
      yLabel: 'V (L)',
      xMin,
      xMax,
      yMax: niceMax(Math.max(...points.map((point) => point.y)), AXIS_ROUNDING_SMALL),
      points,
      dashedBelowX: MEASURED_FROM_K - offset,
      current: { x: state.temperatureK - offset, y: state.volumeL },
      xTicks,
    };
  }
  const points = shift(pressureTemperatureSeries(state.nMol, state.volumeL, temperatureMax));
  return {
    label: 'Model pressure against temperature at constant amount and volume',
    xLabel: celsius ? 'Temperature (°C)' : 'Temperature (K)',
    yLabel: 'p (kPa)',
    xMin,
    xMax,
    yMax: niceMax(Math.max(...points.map((point) => point.y)), AXIS_ROUNDING),
    points,
    dashedBelowX: MEASURED_FROM_K - offset,
    current: { x: state.temperatureK - offset, y: particleModel(state).pressureKPa },
    xTicks,
  };
}

export function GasGraph({
  mode,
  state,
  revealed,
  celsius,
  axis = DEFAULT_AXIS,
}: {
  mode: GasMode;
  state: ExplorerState;
  revealed: boolean;
  celsius: boolean;
  axis?: GasGraphAxis;
}) {
  const spec = graphSpec(mode, state, celsius, axis);
  const toX = (x: number) =>
    PLOT.left + ((x - spec.xMin) / (spec.xMax - spec.xMin)) * (PLOT.right - PLOT.left);
  const toY = (y: number) => PLOT.bottom - (y / spec.yMax) * (PLOT.bottom - PLOT.top);
  const toPoints = (points: GraphPoint[]) =>
    points.map((point) => `${toX(point.x)},${toY(point.y)}`).join(' ');
  const solid =
    spec.dashedBelowX === null
      ? spec.points
      : spec.points.filter((point) => point.x >= (spec.dashedBelowX as number));
  const dashed =
    spec.dashedBelowX === null
      ? []
      : spec.points.filter((point) => point.x <= (spec.dashedBelowX as number));
  const zeroX = toX(0);

  return (
    <svg
      viewBox="0 0 320 196"
      className="w-full overflow-visible"
      role="img"
      aria-label={revealed ? spec.label : `${spec.label} — hidden until you predict`}
    >
      <path
        d={`M${PLOT.left} ${PLOT.top}V${PLOT.bottom}H${PLOT.right}`}
        fill="none"
        stroke="currentColor"
        className="text-ink-soft"
      />
      {celsius && mode !== 'boyle' ? (
        <path
          d={`M${zeroX} ${PLOT.top}V${PLOT.bottom}`}
          fill="none"
          stroke="currentColor"
          strokeDasharray="3 4"
          className="text-hairline"
        />
      ) : null}
      {spec.xTicks.map((tick) => (
        <text
          key={tick}
          x={toX(tick)}
          y={PLOT.bottom + 13}
          fontSize="9"
          textAnchor="middle"
          fill="currentColor"
          className="text-slate2"
        >
          {formatSig(tick, 5)}
        </text>
      ))}
      <text
        x={PLOT.left - 6}
        y={PLOT.top + 4}
        fontSize="9"
        textAnchor="end"
        fill="currentColor"
        className="text-slate2"
      >
        {spec.yMax}
      </text>
      <text
        x={PLOT.left - 6}
        y={PLOT.bottom}
        fontSize="9"
        textAnchor="end"
        fill="currentColor"
        className="text-slate2"
      >
        0
      </text>
      <text
        x={(PLOT.left + PLOT.right) / 2}
        y={PLOT.bottom + 30}
        fontSize="10"
        fontWeight="700"
        textAnchor="middle"
        fill="currentColor"
        className="text-ink"
      >
        {spec.xLabel}
      </text>
      <text
        x={10}
        y={(PLOT.top + PLOT.bottom) / 2}
        fontSize="10"
        fontWeight="700"
        textAnchor="middle"
        fill="currentColor"
        className="text-ink"
        transform={`rotate(-90 10 ${(PLOT.top + PLOT.bottom) / 2})`}
      >
        {spec.yLabel}
      </text>
      {revealed ? (
        <>
          <polyline
            points={toPoints(solid)}
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-brand-sky"
          />
          {dashed.length > 1 ? (
            <polyline
              points={toPoints(dashed)}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeDasharray="5 5"
              className="text-brand-coral"
            />
          ) : null}
          <circle
            cx={toX(spec.current.x)}
            cy={toY(spec.current.y)}
            r="5.5"
            className="fill-brand-coral stroke-canvas-pure"
            strokeWidth="2"
          />
          {dashed.length > 1 ? (
            <text
              x={toX(spec.xMin === 0 ? 0 : -KELVIN_OFFSET) + 4}
              y={PLOT.bottom - 8}
              fontSize="9"
              fontWeight="700"
              fill="currentColor"
              className="text-brand-coral"
            >
              {celsius ? 'absolute zero −273.15 °C' : 'extrapolated to 0 K'}
            </text>
          ) : null}
        </>
      ) : (
        <text
          x={(PLOT.left + PLOT.right) / 2}
          y={(PLOT.top + PLOT.bottom) / 2}
          fontSize="11"
          fontWeight="700"
          textAnchor="middle"
          fill="currentColor"
          className="text-slate2"
        >
          Predict first to reveal the model
        </text>
      )}
    </svg>
  );
}
